// Structure adapted from Huma HRMS data-table (shadcn-admin table, MIT).
import type { RowData } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { Table } from "@/components/ui/table";
import { cn } from "@/utils";
import { DataTableBody, type DataTableStateProps } from "./data-table-body";
import { DataTableFrame } from "./data-table-frame";
import { DataTableHeader } from "./data-table-header";
import { useDataTableState } from "./data-table-state-provider";
import { type DataTableDensity, type DataTableInstance, type UseDataTableOptions, useDataTable } from "./use-data-table";
import { useDataTableColumnPinning } from "./use-data-table-column-pinning";

type DataTableViewProps<TData extends RowData> = DataTableStateProps & {
  className?: string;
  density?: DataTableDensity;
  emptyMessage?: ReactNode;
  footer?: ReactNode;
  table: DataTableInstance<TData>;
  tableClassName?: string;
};

export type DataTableProps<TData extends RowData> = Omit<DataTableViewProps<TData>, "table"> &
  (UseDataTableOptions<TData> | { table: DataTableInstance<TData> });

export function DataTable<TData extends RowData>(props: DataTableProps<TData>) {
  return "table" in props ? <DataTableView {...props} /> : <ManagedDataTable {...props} />;
}

function ManagedDataTable<TData extends RowData>(props: UseDataTableOptions<TData> & Omit<DataTableViewProps<TData>, "table">) {
  const table = useDataTable(props);
  return <DataTableView {...props} table={table} />;
}

function DataTableView<TData extends RowData>({
  className,
  density = "default",
  emptyMessage,
  footer,
  table,
  tableClassName,
  ...overrides
}: DataTableViewProps<TData>) {
  const state = useDataTableState(overrides);
  const hasPinnedColumns = table.getIsSomeColumnsPinned();
  // 保留分组表头和固定列分区，避免迁移 HRMS 的平铺表头后丢失当前能力。
  const headerGroups = hasPinnedColumns ? [table.getStartHeaderGroups(), table.getCenterHeaderGroups(), table.getEndHeaderGroups()] : [table.getHeaderGroups()];
  const layoutKey = JSON.stringify(headerGroups.map((groups) => groups.map((group) => group.headers.map((header) => header.id))));
  const tableRef = useDataTableColumnPinning(hasPinnedColumns, layoutKey);

  return (
    <DataTableFrame className={className} density={density} footer={footer} isBusy={state.isLoading || state.isRefreshing}>
      <Table className={cn("data-table-content border-separate border-spacing-0", hasPinnedColumns && "isolate", tableClassName)} ref={tableRef}>
        <DataTableHeader hasPinnedColumns={hasPinnedColumns} headerGroups={headerGroups} table={table} />
        <DataTableBody {...state} density={density} emptyTitle={state.emptyTitle ?? emptyMessage} table={table} />
      </Table>
    </DataTableFrame>
  );
}
